import { hasSeen, markSeen } from './purchaseDedupStore';
import type { GA4TrackingEvent } from './trackingEventSchema';
import type { PageViewTrackingEvent } from './pageViewTrackingEventSchema';
import type { UserDataEvent } from './eCommerceTrackingEventSchema';
import { clearCatalogSource } from './catalogSourceStorage';
import { clearListContext } from './listContextStorage';

export enum ConsentAction {
  GRANTED = 'granted',
  DENIED = 'denied'
}

export type WindowWithDataLayer = Window & {
  dataLayer?: Array<object>;
  gtag?: (...args: Array<unknown>) => void;
};

export enum TrackingEvent {
  APPLICATION = 'application',
  CONSENT = 'consent',
  CUSTOMER = 'customer',
  ECOMMERCE = 'ecommerce',
  SEARCH = 'search',
  SELECT_SEARCH_SUGGESTION = 'select_search_suggestion',
  SESSION = 'session',
  USER_DATA = 'user_data'
}

export type AdditionalData = Record<string, unknown>;
export type CartTypeQuantityData = Record<string, number>;

export interface BaseEvent {
  readonly anonymousId?: string | null;
  readonly authenticatedId?: string | null;
}

export interface ConsentEvent {
  readonly type: TrackingEvent.CONSENT;
  readonly consent: string;
  readonly settings: Record<string, 'granted' | 'denied'>;
}

export interface TrackingEcommerceEvent {
  readonly type: TrackingEvent.ECOMMERCE;
  readonly event: string;
  readonly ecommerce: AdditionalData;
  readonly session?: {
    cart?: CartTypeQuantityData | undefined;
  };
}

export type TrackingUserDataEvent = UserDataEvent & {
  readonly type: TrackingEvent.USER_DATA;
};

export interface TrackingCustomer {
  readonly type: TrackingEvent.CUSTOMER;
  readonly event: string;
}

interface TrackingSession {
  readonly cart: CartTypeQuantityData;
}

export interface TrackingInitEvent {
  readonly type: TrackingEvent.APPLICATION;
  readonly event: string;
  readonly remoteAddress: string;
  readonly loggedIn: boolean;
  readonly session?: TrackingSession;
  readonly customerId?: string;
  readonly experimentId?: string;
}

export interface SearchEventMetadata {
  readonly attributionToken?: string;
  readonly index?: string;
  readonly item_list_id?: string;
  readonly items: ReadonlyArray<{ readonly item_id: string }>;
  readonly provider?: string;
  readonly query?: string;
  readonly queryId?: string;
  readonly totalCount?: string;
}

export interface TrackingSearchEvent {
  readonly event: TrackingEvent.SEARCH;
  readonly search_term: string;
  readonly search_results: number;
  readonly page_type: string;
  readonly searchMetadata: SearchEventMetadata;
}

export enum SearchSuggestionType {
  BRAND = 'BRAND',
  CATEGORY = 'CATEGORY',
  PRODUCT = 'PRODUCT',
  SEARCH_TERM = 'SEARCH_TERM',
  SUB_BRAND = 'SUB_BRAND'
}

export interface TrackingSelectSearchSuggestionEvent {
  readonly event: TrackingEvent.SELECT_SEARCH_SUGGESTION;
  readonly search_suggestion_name: string;
  readonly search_suggestion_query: string;
  readonly search_suggestion_type: SearchSuggestionType;
  readonly page_type?: string;
}

export interface TrackingSessionEvent {
  readonly type: TrackingEvent.SESSION;
  readonly event: string;
  readonly session: TrackingSession;
}

export type TrackingCustomerEvent = TrackingCustomer & AdditionalData;

export type TrackEvent =
  | TrackingEcommerceEvent
  | ConsentEvent
  | TrackingCustomerEvent
  | TrackingSessionEvent;

export type PushEventType = (TrackEvent & BaseEvent) | TrackingInitEvent;

// The frontend is a pure consent-*timing* gate. Every event is held in a single queue until
// the *explicit* `consent_status` has been processed AND a `custom_page_view` is present to lead
// the flush; then the whole queue is released unconditionally (grant or deny — no discarding).
// GTM Consent Mode does the per-tag blocking; the frontend performs no consent-based filtering.
const CONSENT_POLL_INTERVAL_MS = 2000;
const CONSENT_POLL_MAX_MS = 10000;

type TrackingEventToPush =
  | PushEventType
  | TrackingSearchEvent
  | TrackingSelectSearchSuggestionEvent
  | TrackingUserDataEvent
  | GA4TrackingEvent
  | PageViewTrackingEvent;

const getPurchaseTransactionId = (event: object): string | null => {
  if (!('event_name' in event) || (event as { event_name?: unknown }).event_name !== 'purchase') {
    return null;
  }
  const { ecommerce } = event as { ecommerce?: unknown };
  if (typeof ecommerce !== 'object' || ecommerce === null) {
    return null;
  }
  const { transaction_id: transactionId } = ecommerce as { transaction_id?: unknown };
  return typeof transactionId === 'string' && transactionId.length > 0 ? transactionId : null;
};

// Apply delivery-time guards only after the consent queue releases an event. Resetting the nested
// search object prevents GTM from merging values from an earlier search into the current event.
const pushEventToDataLayer = (dataLayer: Array<object>, event: object): void => {
  if ('event' in event && event.event === TrackingEvent.SEARCH) {
    dataLayer.push({ searchMetadata: null });
  }

  const transactionId = getPurchaseTransactionId(event);

  if (transactionId === null) {
    dataLayer.push(event);
  } else if (!hasSeen(transactionId)) {
    dataLayer.push(event);
    markSeen(transactionId);
  }
};

// The queue is released only by an *explicit* `consent_status`. An implicit one
// (`type === 'IMPLICIT'`, emitted on initial page load) must not release it.
const isExplicitConsentStatusEntry = (entry: object): boolean =>
  entry !== null &&
  typeof entry === 'object' &&
  'event' in entry &&
  entry.event === 'consent_status' &&
  'type' in entry &&
  (entry as { type?: unknown }).type === 'EXPLICIT';

const hasConsentStatusEvent = (dataLayer: Array<object>): boolean =>
  dataLayer.some(isExplicitConsentStatusEntry);

const isCustomPageViewEvent = (event: object): boolean =>
  'event' in event && (event as { event?: unknown }).event === 'custom_page_view';

export let trackingQueue: Array<TrackingEventToPush> = [];
export let consentStatusEventSeen = false;
let pageViewReleased = false;
let trackingPollTimer: number | null = null;
let consentSeenAt: number | null = null;

const ensureDataLayer = (): Array<object> => {
  const win = window as WindowWithDataLayer;
  if (!win.dataLayer) {
    win.dataLayer = [];
  }
  return win.dataLayer;
};

export const stopTrackingPolling = (): void => {
  if (trackingPollTimer !== null) {
    clearInterval(trackingPollTimer);
    trackingPollTimer = null;
  }
};

export const clearTrackingQueue = (): void => {
  trackingQueue = [];
};

export const clearConsentStatusEventSeen = (): void => {
  consentStatusEventSeen = false;
  consentSeenAt = null;
};

export const setConsentStatusEventSeenForTesting = (seen: boolean): void => {
  consentStatusEventSeen = seen;
  consentSeenAt = seen ? Date.now() : null;
};

export const clearPageViewReleased = (): void => {
  pageViewReleased = false;
};

export const setPageViewReleasedForTesting = (released: boolean): void => {
  pageViewReleased = released;
};

// Release requires BOTH signals: the explicit `consent_status` has been seen AND a
// `custom_page_view` is queued to lead the flush (AC3/A4). Bounded by the 10s poll ceiling so a
// degenerate load with no page view can never buffer forever.
const canRelease = (): boolean => {
  if (!consentStatusEventSeen) {
    return false;
  }
  if (trackingQueue.some(isCustomPageViewEvent)) {
    return true;
  }
  return consentSeenAt !== null && Date.now() - consentSeenAt >= CONSENT_POLL_MAX_MS;
};

// Flush the queue leading with `custom_page_view`, then the remaining events in order. No event
// is discarded regardless of grant vs deny — GTM Consent Mode does the per-tag blocking.
const flushTrackingQueue = (): void => {
  const dataLayer = ensureDataLayer();
  const pageViewIndex = trackingQueue.findIndex(isCustomPageViewEvent);
  if (pageViewIndex > 0) {
    const [pageView] = trackingQueue.splice(pageViewIndex, 1);
    trackingQueue.unshift(pageView);
  }
  while (trackingQueue.length > 0) {
    const event = trackingQueue.shift();
    if (event) {
      pushEventToDataLayer(dataLayer, event);
    }
  }
  pageViewReleased = true;
};

const markConsentSeenIfPresent = (dataLayer: Array<object> | undefined): void => {
  if (!consentStatusEventSeen && dataLayer && hasConsentStatusEvent(dataLayer)) {
    consentStatusEventSeen = true;
    consentSeenAt = Date.now();
  }
};

const startTrackingPollingIfNeeded = (): void => {
  if (trackingPollTimer === null) {
    trackingPollTimer = window.setInterval(() => {
      const win = window as WindowWithDataLayer;
      markConsentSeenIfPresent(win.dataLayer);
      if (canRelease()) {
        flushTrackingQueue();
        stopTrackingPolling();
      }
    }, CONSENT_POLL_INTERVAL_MS);
  }
};

const pushToDataLayer = (event: TrackingEventToPush): void => {
  // Consent forwarding to GTM (A6): forward the banner interaction to `gtag('consent', …)`.
  // This is the only side effect of a CONSENT event — the frontend does no queue filtering.
  if ('type' in event && event.type === TrackingEvent.CONSENT) {
    const { gtag } = window as WindowWithDataLayer;
    if (gtag) {
      gtag(event.type, event.consent, event.settings);
    }

    // Consent withdrawal: drop any stored list-context and catalog attribution (NFR-FT-001, AC8).
    // The frontend keeps no consent-based queue filtering (GTM Consent Mode does per-tag blocking);
    // wiping the persisted attribution stores is the only consent side effect beyond the gtag forward.
    if (event.settings) {
      const values = Object.values(event.settings);
      const allDenied = values.length > 0 && values.every((v) => v === ConsentAction.DENIED);
      if (allDenied) {
        clearListContext();
        clearCatalogSource();
      }
    }

    return;
  }

  const dataLayer = ensureDataLayer();
  markConsentSeenIfPresent(dataLayer);

  if (pageViewReleased) {
    pushEventToDataLayer(dataLayer, event);
    return;
  }

  trackingQueue.push(event);
  if (canRelease()) {
    flushTrackingQueue();
    stopTrackingPolling();
  } else {
    startTrackingPollingIfNeeded();
  }
};

export default pushToDataLayer;
